import type { OnChangeFn, SortingState, Updater } from '@tanstack/react-table';
import { useCallback, useRef } from 'react';

import { useMemoDeepEqual, useMemoDeepEqualValue } from '@/lib/hooks/use-memo-deep-equal';
import { SortOrder } from '@/server/types/list-view-types';

export type SortFilter<TField, TDirection extends 'ASC' | 'DESC' = SortOrder> = {
    field: TField;
    direction: TDirection;
};

export type UseGenericListViewSortingHook = [state: SortingState, setState: OnChangeFn<SortingState>];

export function useGenericListViewSorting<
    TField extends string,
    TDirection extends 'ASC' | 'DESC' = SortOrder,
>(
    sortingFilter: SortFilter<TField, TDirection> | null | undefined,
    setSortingFilter: (sortingFilter: SortFilter<TField, TDirection> | undefined) => void,
    sortBy: Record<string, TField>,
): UseGenericListViewSortingHook {
    const lastSortRef = useRef<SortingState>([]);
    const validSorts = useMemoDeepEqualValue(Object.values(sortBy));

    // this builds a @tanstack/react-table SortingState from the given params
    const sorting = useMemoDeepEqual(() => {
        if (sortingFilter) {
            lastSortRef.current = [
                {
                    id: sortingFilter.field,
                    desc: sortingFilter.direction === SortOrder.DESC,
                },
            ];
        } else {
            lastSortRef.current = [];
        }
        return lastSortRef.current;
    }, [sortingFilter]);

    // this creates a @tanstack/react-table callback for onSortingChange
    const setState = useCallback(
        (sortingState: Updater<SortingState>) => {
            let nextState: SortingState;
            if (typeof sortingState === 'function') {
                nextState = sortingState(lastSortRef.current);
            } else {
                nextState = sortingState;
            }

            const sort = nextState[0];
            if (sort) {
                const isValidSortBy = validSorts.includes(sort.id as TField);
                if (isValidSortBy) {
                    setSortingFilter({
                        field: sort.id as TField,
                        direction: (sort.desc ? SortOrder.DESC : SortOrder.ASC) as TDirection,
                    });
                }
            } else {
                setSortingFilter(undefined);
            }
        },
        [validSorts, setSortingFilter],
    );

    return [sorting, setState];
}
